import { toPublicAssetUrl, uploadSignedAsset } from "./uploadSignedAsset";

const REHOST_CONCURRENCY = 4;

const UNLAYER_HOST_RE = /(?:assets\.unlayer\.com|unlayer-assets-prod\.s3[.\w-]*)/i;

const UNLAYER_URL_RE = /https:\/\/(?:assets\.unlayer\.com|unlayer-assets-prod\.s3[.\w-]*)\/[^\s"'\\<>]*/gi;

export const isUnlayerAssetUrl = (value) => typeof value === "string" && UNLAYER_HOST_RE.test(value);

export const normalizeUnlayerUrl = (url) => {
  try {
    const parsed = new URL(url);
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    return url?.split("?")[0] ?? url;
  }
};

const collectUrlsFromString = (value, bucket) => {
  if (typeof value !== "string" || !isUnlayerAssetUrl(value)) {
    return;
  }

  const matches = value.match(UNLAYER_URL_RE);
  if (matches?.length) {
    matches.forEach((url) => bucket.add(normalizeUnlayerUrl(url)));
    return;
  }

  const trimmed = value.trim();
  if (/^https:\/\//i.test(trimmed) && isUnlayerAssetUrl(trimmed)) {
    bucket.add(normalizeUnlayerUrl(trimmed));
  }
};

const collectUnlayerUrls = (design, html) => {
  const urls = new Set();

  const walk = (node) => {
    if (typeof node === "string") {
      collectUrlsFromString(node, urls);
      return;
    }

    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }

    if (node && typeof node === "object") {
      Object.values(node).forEach(walk);
    }
  };

  walk(design);
  collectUrlsFromString(html, urls);

  return [...urls];
};

const replaceUnlayerUrlsInString = (value, urlMap) => {
  if (typeof value !== "string" || !isUnlayerAssetUrl(value)) {
    return value;
  }

  let next = value;

  next = next.replace(UNLAYER_URL_RE, (matched) => {
    const mapped = urlMap.get(normalizeUnlayerUrl(matched));
    return mapped || matched;
  });

  if (urlMap.has(normalizeUnlayerUrl(next))) {
    return urlMap.get(normalizeUnlayerUrl(next));
  }

  return next;
};

const replaceUnlayerUrlsDeep = (node, urlMap) => {
  if (typeof node === "string") {
    return replaceUnlayerUrlsInString(node, urlMap);
  }

  if (Array.isArray(node)) {
    return node.map((item) => replaceUnlayerUrlsDeep(item, urlMap));
  }

  if (node && typeof node === "object") {
    return Object.fromEntries(Object.entries(node).map(([key, value]) => [key, replaceUnlayerUrlsDeep(value, urlMap)]));
  }

  return node;
};

const extensionFromContentType = (contentType, fallbackUrl) => {
  const map = {
    "image/jpeg": "jpg",
    "image/jpg": "jpg",
    "image/png": "png",
    "image/gif": "gif",
    "image/webp": "webp",
  };

  if (contentType && map[contentType.split(";")[0].trim().toLowerCase()]) {
    return map[contentType.split(";")[0].trim().toLowerCase()];
  }

  try {
    const pathname = new URL(fallbackUrl).pathname;
    const ext = pathname.split(".").pop()?.toLowerCase();
    if (ext && ["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) {
      return ext === "jpeg" ? "jpg" : ext;
    }
  } catch {
    // ignore
  }

  return "jpg";
};

const filenameFromUrl = (url, extension) => {
  try {
    const name = decodeURIComponent(new URL(url).pathname.split("/").pop() || "image");
    if (name.toLowerCase().endsWith(`.${extension}`)) {
      return name;
    }
    const base = name.includes(".") ? name.replace(/\.[^.]+$/, "") : name;
    return `${base || "image"}.${extension}`;
  } catch {
    return `image.${extension}`;
  }
};

const candidateDownloadUrls = (url) => {
  const normalized = normalizeUnlayerUrl(url);
  return [
    // Unlayer tools proxy bypasses CDN CORS (same endpoint the editor uses)
    `https://api.tools.unlayer.com/v1/image?url=${encodeURIComponent(normalized)}`,
    normalized,
  ];
};

const fetchBlob = async (url) => {
  const response = await fetch(url, {
    method: "GET",
    credentials: "omit",
    mode: "cors",
  });

  if (!response.ok) {
    throw new Error(`Failed to download image: ${response.status}`);
  }

  const blob = await response.blob();
  const contentType = blob.type || response.headers.get("content-type") || "image/jpeg";
  return { blob, contentType };
};

const fetchUnlayerImageAsFile = async (url) => {
  let lastError;

  for (const candidate of candidateDownloadUrls(url)) {
    try {
      const { blob, contentType } = await fetchBlob(candidate);
      const extension = extensionFromContentType(contentType, url);
      const filename = filenameFromUrl(url, extension);
      return new File([blob], filename, {
        type: contentType.startsWith("image/") ? contentType : `image/${extension}`,
      });
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error(`Failed to download Unlayer image: ${url}`);
};

const rehostSingleUrl = async (sourceUrl, description) => {
  const file = await fetchUnlayerImageAsFile(sourceUrl);
  const asset = await uploadSignedAsset({
    file,
    entity: "TEMPLATE",
    description,
  });
  const publicUrl = toPublicAssetUrl(asset?.url);

  if (!publicUrl) {
    throw new Error(`Empty CDN url after rehost for ${sourceUrl}`);
  }

  return publicUrl;
};

const mapSettledWithLimit = async (items, limit, mapper) => {
  const results = new Array(items.length);
  let nextIndex = 0;

  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (nextIndex < items.length) {
      const index = nextIndex;
      nextIndex += 1;

      try {
        results[index] = { status: "fulfilled", value: await mapper(items[index], index) };
      } catch (reason) {
        results[index] = { status: "rejected", reason };
      }
    }
  });

  await Promise.all(workers);
  return results;
};

/**
 * Download Unlayer-hosted images from design/HTML, upload to our CDN, replace URLs.
 * Caller should abort Save when failedUrls is non-empty.
 */
export const rehostUnlayerImages = async (design, html, { description = "" } = {}) => {
  const unlayerUrls = collectUnlayerUrls(design, html);

  if (!unlayerUrls.length) {
    return { design, html, replacedCount: 0, failedUrls: [] };
  }

  const urlMap = new Map();
  const failedUrls = [];

  const results = await mapSettledWithLimit(unlayerUrls, REHOST_CONCURRENCY, async (sourceUrl) => {
    const publicUrl = await rehostSingleUrl(sourceUrl, description);
    return { sourceUrl, publicUrl };
  });

  results.forEach((result, index) => {
    const sourceUrl = unlayerUrls[index];

    if (result.status === "fulfilled") {
      urlMap.set(result.value.sourceUrl, result.value.publicUrl);
      return;
    }

    failedUrls.push(sourceUrl);
    console.warn("Unlayer image rehost failed:", sourceUrl, result.reason);
  });

  if (!urlMap.size) {
    return { design, html, replacedCount: 0, failedUrls };
  }

  return {
    design: replaceUnlayerUrlsDeep(design, urlMap),
    html: replaceUnlayerUrlsInString(html, urlMap),
    replacedCount: urlMap.size,
    failedUrls,
  };
};

export default rehostUnlayerImages;
